import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { messages } from '../app/messages';
import { firebaseConfigStatus } from '../lib/firebase';
import {
  IDLE_ACCOUNT_ACCESS,
  LOADING_ACCOUNT_ACCESS,
  resolveAccountAccess,
  type AccountAccessState,
  type ResolvedAccountAccessState,
} from './access';
import { traceSession, traceSignInStep } from './signInTrace';
import {
  initialSessionState,
  signInWithGoogle,
  signOut as signOutOfFirebase,
  subscribeToSession,
  type SessionState,
} from './session';

/**
 * The session, made available to the whole shell — T007.
 *
 * The provider owns exactly three things: the current `SessionState`, whether a sign-in or sign-out
 * is in flight, and the one Georgian line to show if either failed. It deliberately owns no
 * permission, no role and no account state: those are server decisions (FR-AUTH-03), and a client
 * cache of them would be a second, wrong source of truth.
 */

export type AuthPending = 'signIn' | 'signOut' | null;

export interface AuthContextValue {
  readonly state: SessionState;
  /** A server response, never a permission inferred by the client. */
  readonly access: AccountAccessState;
  readonly pending: AuthPending;
  /** A Georgian line from the catalogue, or `null`. Never a Firebase message. */
  readonly notice: string | null;
  readonly signIn: () => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly refreshAccess: () => Promise<void>;
  readonly dismissNotice: () => void;
}

/**
 * Exported so a test can render the frame in a chosen session state without a Firebase project.
 * Product code uses `<AuthProvider>` and `useSession()`; nothing else should provide this context.
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<SessionState>(initialSessionState);
  const [accessResolution, setAccessResolution] = useState<{
    readonly uid: string;
    readonly state: ResolvedAccountAccessState;
  } | null>(null);
  const accessRequest = useRef(0);
  const [pending, setPending] = useState<AuthPending>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    /*
     * An unconfigured build subscribes to nothing, initializes nothing and contacts nothing — the
     * `unconfigured` state came from `initialSessionState()` during the first render, so this effect
     * has nothing to do. The guard is also what makes the subscription total: `getFirebaseApp()`
     * throws only when the configuration is incomplete, which this branch has already excluded, so
     * there is no failure here to catch and re-render into a state React already holds.
     */
    if (!firebaseConfigStatus().configured) return;
    return subscribeToSession((next) => {
      traceSession(next.status === 'signedIn' ? 'in' : next.status === 'signedOut' ? 'out' : 'loading');
      if (next.status !== 'signedIn') {
        accessRequest.current += 1;
        setAccessResolution(null);
      }
      setState(next);
    });
  }, []);

  const signedInUid = state.status === 'signedIn' ? state.user.uid : null;

  useEffect(() => {
    if (signedInUid === null) return;
    const requestId = ++accessRequest.current;
    void resolveAccountAccess(async () => (await import('../lib/api')).bootstrapCurrentAccount()).then((resolved) => {
      traceSignInStep(
        resolved.status === 'ready' ? 'access' : 'access_error',
        resolved.status === 'ready' ? `${resolved.account.status}/${resolved.account.role}` : resolved.messageKey,
      );
      if (requestId === accessRequest.current) {
        setAccessResolution({ uid: signedInUid, state: resolved });
      }
    });
    return () => {
      if (requestId === accessRequest.current) accessRequest.current += 1;
    };
  }, [signedInUid]);

  const access: AccountAccessState =
    signedInUid === null
      ? IDLE_ACCOUNT_ACCESS
      : accessResolution?.uid === signedInUid
        ? accessResolution.state
        : LOADING_ACCOUNT_ACCESS;

  const signIn = useCallback(async () => {
    setNotice(null);
    setAccessResolution(null);
    setPending('signIn');
    try {
      const outcome = await signInWithGoogle();
      if (outcome === 'failed') setNotice(messages.auth.signInFailed);
      if (outcome === 'cancelled') setNotice(messages.auth.signInCancelled);
    } finally {
      setPending(null);
    }
  }, []);

  const signOut = useCallback(async () => {
    setNotice(null);
    accessRequest.current += 1;
    setAccessResolution(null);
    setPending('signOut');
    traceSignInStep('signout');
    try {
      await signOutOfFirebase();
    } catch {
      setNotice(messages.auth.signOutFailed);
      // The session is still signed in: resolve its access again, or the frame would wait on the
      // access check that was cleared above for a sign-out that did not happen.
      if (signedInUid !== null) {
        const requestId = ++accessRequest.current;
        const resolved = await resolveAccountAccess(async () => (await import('../lib/api')).readCurrentAccountState());
        if (requestId === accessRequest.current) setAccessResolution({ uid: signedInUid, state: resolved });
      }
    } finally {
      setPending(null);
    }
  }, [signedInUid]);

  const refreshAccess = useCallback(async () => {
    if (signedInUid === null) return;
    const requestId = ++accessRequest.current;
    setAccessResolution(null);
    const resolved = await resolveAccountAccess(async () => (await import('../lib/api')).readCurrentAccountState());
    if (requestId === accessRequest.current) {
      setAccessResolution({ uid: signedInUid, state: resolved });
    }
  }, [signedInUid]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, access, pending, notice, signIn, signOut, refreshAccess, dismissNotice }),
    [state, access, pending, notice, signIn, signOut, refreshAccess, dismissNotice],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useSession(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useSession was called outside <AuthProvider>.');
  }
  return value;
}
